/**
 * Layout Dependency Utilities
 *
 * Enforces the AutoPopulation dependency graph in the Layout Editor. First-level
 * attributes (with no `dependencies`, e.g., EntityName) must be placed before any
 * attributes that depend on them. This mirrors how AutoPopulationProvider resolves
 * attributes at execution time: an attribute is looked up by its "clean" id (the
 * part after the task prefix, if any), and its `dependencies` array names other
 * clean ids that must be supplied first.
 */

/** Shape of an AutoPopulation definition entry as returned by
 *  GET /autoPopulation/definitions/{workflowId}. */
export interface AutoPopulationDefinitionEntry {
	autoPopulationId?: string;
	autoPopulationType?: 'STATIC' | 'DYNAMIC' | 'EXTERNAL' | 'NOOP' | string;
	dependencies?: string[];
	dependents?: string[];
	description?: string;
	isOverridable?: boolean;
	operationId?: string;
	requestMapping?: Record<string, string>;
	responseMapping?: Record<string, string>;
}

export type AutoPopulationDefinitionMap = Record<string, AutoPopulationDefinitionEntry>;

/**
 * Strip an optional task prefix from a layout field id. Layout field ids are
 * either the raw attribute id (e.g. "EntityName") for common/system fields or
 * "{taskId}.{attributeId}" for task-scoped fields. AutoPopulation definitions
 * are keyed on the clean attribute id.
 */
export function getCleanAttributeId(fieldId: string): string {
	const dotIndex = fieldId.indexOf('.');
	return dotIndex === -1 ? fieldId : fieldId.slice(dotIndex + 1);
}

/** Build a set of clean attribute ids currently placed anywhere in the layout. */
export function getPlacedCleanIds(placedFieldIds: Iterable<string>): Set<string> {
	const set = new Set<string>();
	for (const id of placedFieldIds) {
		set.add(getCleanAttributeId(id));
	}
	return set;
}

/**
 * Return the list of dependency attribute ids that are not yet placed in the
 * layout. An empty array means the field is free to add.
 *
 * Considers BOTH signals from the AutoPopulation graph:
 *  1. This field's own `dependencies` array.
 *  2. Any other definition whose `dependents` array lists this field — a reverse
 *     signal that the other attribute is effectively a prerequisite even when
 *     this field's own `dependencies` is empty.
 *
 * Example: given EntityName.dependents = ["libby_id","fc_state"] and
 * fc_state.dependencies = [], fc_state is still blocked until EntityName is
 * placed because EntityName declares fc_state as a downstream attribute.
 */
export function getUnmetDependencies(
	fieldId: string,
	placedFieldIds: Iterable<string>,
	autoPopulationDefinitions: AutoPopulationDefinitionMap | undefined,
): string[] {
	if (!autoPopulationDefinitions) return [];
	const cleanId = getCleanAttributeId(fieldId);
	const placedCleanIds = getPlacedCleanIds(placedFieldIds);

	const prerequisites = new Set<string>();

	// Signal 1: this field's own declared dependencies.
	const ownDeps = autoPopulationDefinitions[cleanId]?.dependencies;
	if (ownDeps) {
		for (const dep of ownDeps) {
			if (dep && dep !== cleanId) prerequisites.add(dep);
		}
	}

	// Signal 2: any parent whose `dependents` list references this field.
	for (const [parentId, def] of Object.entries(autoPopulationDefinitions)) {
		if (parentId === cleanId) continue;
		if (def?.dependents?.includes(cleanId)) {
			prerequisites.add(parentId);
		}
	}

	if (prerequisites.size === 0) return [];
	return Array.from(prerequisites).filter(dep => !placedCleanIds.has(dep));
}

/**
 * Return the list of placed dependents for a given field. Used to block removal
 * of a first-level attribute while its downstream attributes are still placed.
 *
 * Considers BOTH signals from the AutoPopulation graph:
 *  1. Any placed field whose own `dependencies` array includes this field.
 *  2. Any entry from this field's `dependents` list that is currently placed.
 *
 * These indexes can drift (e.g. EntityName.dependents lists fc_state while
 * fc_state.dependencies is empty), so the union keeps removal safe: as long as
 * either side declares a link, we treat the placed downstream field as a real
 * dependent and block removal.
 */
export function getPlacedDependents(
	fieldId: string,
	placedFieldIds: Iterable<string>,
	autoPopulationDefinitions: AutoPopulationDefinitionMap | undefined,
): string[] {
	if (!autoPopulationDefinitions) return [];
	const cleanId = getCleanAttributeId(fieldId);

	// Build a set of placed clean ids once for O(1) lookups.
	const placedCleanIds = getPlacedCleanIds(placedFieldIds);
	placedCleanIds.delete(cleanId); // exclude self

	const dependents = new Set<string>();

	// Signal 1: forward — placed fields whose dependencies include this one.
	for (const placedCleanId of placedCleanIds) {
		const placedDef = autoPopulationDefinitions[placedCleanId];
		if (placedDef?.dependencies?.includes(cleanId)) {
			dependents.add(placedCleanId);
		}
	}

	// Signal 2: reverse — this field's dependents list, intersected with placed.
	const declaredDependents = autoPopulationDefinitions[cleanId]?.dependents;
	if (declaredDependents) {
		for (const dep of declaredDependents) {
			if (dep !== cleanId && placedCleanIds.has(dep)) {
				dependents.add(dep);
			}
		}
	}

	return Array.from(dependents);
}
